
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useProperties } from '@/hooks/useProperties';
import { useUserManagement } from '@/hooks/useUserManagement';

interface AssignPropertiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  owner: any;
}

const AssignPropertiesModal = ({ isOpen, onClose, onSuccess, owner }: AssignPropertiesModalProps) => {
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const { data: properties = [], isLoading } = useProperties();
  const { assignProperties, loading } = useUserManagement();

  // Initialize selected properties when modal opens
  useEffect(() => {
    if (owner?.user_property_assignments) {
      const assignedIds = owner.user_property_assignments.map((assignment: any) => assignment.property_id);
      setSelectedProperties(assignedIds);
    }
  }, [owner]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { error } = await assignProperties(owner.user_id, selectedProperties);

    if (!error) {
      onSuccess();
    }
  };

  const handlePropertyToggle = (propertyId: string, checked: boolean) => {
    if (checked) {
      setSelectedProperties(prev => [...prev, propertyId]);
    } else {
      setSelectedProperties(prev => prev.filter(id => id !== propertyId));
    }
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg">
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-500">Cargando propiedades...</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Asignar Propiedades - {owner.first_name} {owner.last_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="max-h-96 overflow-y-auto space-y-3">
            {properties.map((property) => {
              const locationDisplay = property.neighborhood ? `${property.neighborhood.city}, ${property.neighborhood.state}` : '';
              return (
                <div key={property.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                  <Checkbox
                    id={property.id}
                    checked={selectedProperties.includes(property.id)}
                    onCheckedChange={(checked) => handlePropertyToggle(property.id, checked as boolean)}
                  />
                  <label htmlFor={property.id} className="flex-1 cursor-pointer">
                    <div className="font-medium text-gray-900">{property.title}</div>
                    <div className="text-sm text-gray-600">{property.shortName}</div>
                    <div className="text-sm text-gray-500">{locationDisplay}</div>
                  </label>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-600">
              {selectedProperties.length} de {properties.length} propiedades seleccionadas
            </div>
            <div className="flex space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar Asignaciones'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignPropertiesModal;
